import { useEffect } from "react";

import { useForm } from "react-hook-form";

import dayjs from "dayjs";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import type {
  ExchangeRate,
  ManualOverrideFormData,
} from "~/types/exchangeRates";

interface ExchangeRateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rate?: ExchangeRate;
  onSubmit: (data: ManualOverrideFormData) => void;
  isLoading: boolean;
}

export function ExchangeRateDialog({
  open,
  onOpenChange,
  rate,
  onSubmit,
  isLoading,
}: ExchangeRateDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ManualOverrideFormData>({
    defaultValues: {
      rate_date: dayjs().format("YYYY-MM-DD"),
      usd_clp: 0,
      notes: "",
    },
  });

  useEffect(() => {
    if (rate) {
      reset({
        rate_date: rate.rate_date,
        usd_clp: rate.usd_clp,
        notes: rate.notes || "",
      });
    } else {
      reset({
        rate_date: dayjs().format("YYYY-MM-DD"),
        usd_clp: 0,
        notes: "",
      });
    }
  }, [rate, reset]);

  const handleFormSubmit = (data: ManualOverrideFormData) => {
    onSubmit({
      ...data,
      usd_clp: Number(data.usd_clp),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {rate ? "Editar Tipo de Cambio" : "Agregar Tipo de Cambio Manual"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="rate_date">Fecha</Label>
              <Input
                id="rate_date"
                type="date"
                {...register("rate_date", {
                  required: "La fecha es requerida",
                })}
              />
              {errors.rate_date && (
                <p className="text-sm text-destructive">
                  {errors.rate_date.message}
                </p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="usd_clp">Tipo de Cambio (USD/CLP)</Label>
              <Input
                id="usd_clp"
                type="number"
                step="0.0001"
                placeholder="950.00"
                {...register("usd_clp", {
                  required: "El tipo de cambio es requerido",
                  min: { value: 0.01, message: "Debe ser mayor a 0" },
                })}
              />
              {errors.usd_clp && (
                <p className="text-sm text-destructive">
                  {errors.usd_clp.message}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Valor de 1 USD en pesos chilenos
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">Notas (opcional)</Label>
              <Textarea
                id="notes"
                placeholder="Motivo de la corrección manual..."
                {...register("notes")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
